import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import {
  CEASURI_SLA, STARI_PROPUNERE, STARI_TICHET, STARI_VIZITA, eticheta,
} from "./enums.js";
import { dataOra } from "./text.js";
import { tr } from "./i18n.js";
import DosarInterventie from "./DosarInterventie.jsx";

/**
 * Listele de valori se copiază din `schema.prisma`, niciodată din memorie.
 * Categoriile plauzibile scrise din cap au fost respinse de server cu 400, iar
 * suita n-avea cum să le prindă: ea nu trece prin dropdownuri.
 */
const TIPURI = [
  ["Incident", "Incident — ceva s-a stricat"],
  ["Cerinta", "Cerință — o solicitare nouă"],
];
const SEVERITATI = ["Critic", "Major", "Important", "Minor"];
const CANALE = ["Portal", "Email", "Telefon", "Api"];

// Etichetele au plecat în `enums.js` la al doilea consumator (portalul
// clientului): o listă de etichete care trăiește într-un singur ecran scurge
// valoarea brută în toate celelalte.
const STARI = STARI_TICHET;
const CEASURI = CEASURI_SLA;
/** Reacția vine înaintea restaurării. Ordinea din bază nu e una de citit. */
const ordineCeas = (l = []) => [...l].sort(
  (a, b) => (a.tip === "Reactie" ? 0 : 1) - (b.tip === "Reactie" ? 0 : 1));

const DISPECERAT = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const eClient = (rol) => String(rol).startsWith("CLIENT_");


/** Un termen se citește în ore și minute, nu în 437 de minute. */
function durata(minute) {
  const m = Math.abs(Math.round(minute));
  const zile = Math.floor(m / 1440);
  const ore = Math.floor((m % 1440) / 60);
  const min = m % 60;
  if (zile) return `${zile} z ${ore} h`;
  if (ore) return `${ore} h ${String(min).padStart(2, "0")} min`;
  return `${min} min`;
}

function Ceas({ c }) {
  const clasa = c.stare === "Depasit" ? "ceas-depasit"
    : c.stare === "Avertizare" ? "ceas-avertizare"
      : c.stare === "Oprit" ? "ceas-oprit" : "ceas-in-termen";
  return (
    <div className={"ceas " + clasa} title={tr("Termen: {0}", dataOra(c.termenLa))}>
      <span className="ceas-tip">{tr(CEASURI[c.tip] ?? c.tip)}</span>
      <span className="ceas-timp">
        {c.stare === "Oprit" ? tr("oprit")
          : c.minuteRamase < 0 ? tr("depășit cu {0}", durata(c.minuteRamase))
            : durata(c.minuteRamase)}
      </span>
      <span className="ceas-bara">
        <i style={{ width: `${Math.min(100, c.procentConsumat)}%` }} />
      </span>
      {c.inPauza && <span className="marcaj">{tr("în pauză")}</span>}
    </div>
  );
}

export default function Tichete({ eu }) {
  const [lista, setLista] = useState([]);
  // Istoricul întreg se plafonează. Tăcerea ar fi chiar defectul.
  const [taiat, setTaiat] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [doarDeschise, setDoarDeschise] = useState(true);
  const [deschis, setDeschis] = useState(null);
  const [formNou, setFormNou] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const dispecerat = DISPECERAT.includes(eu.rol);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      const r = await api.tichete({ clientId: clientId || undefined, deschise: doarDeschise });
      setLista(r.tichete ?? []);
      // Istoricul întreg se plafonează; coada de tichete deschise, niciodată.
      setTaiat(r.plafon && r.total > (r.tichete?.length ?? 0) ? r : null);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [clientId, doarDeschise]);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => { api.clienti().then(setClienti).catch(() => {}); }, []);

  const deschide = async (id) => {
    try { setDeschis(await api.tichet(id)); }
    catch (e) { setEroare(mesajEroare(e)); }
  };

  return (
    <div className="continut">
      <h1>{tr("Tichete")}</h1>
      <p className="subtitlu">
        {tr("Coada se citește după cât timp a mai rămas, nu după când a intrat tichetul: cel mai aproape de depășire stă primul. Termenele sunt înghețate la deschidere — o reclasificare de mai târziu nu rescrie o promisiune deja făcută.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            {!eClient(eu.rol) && (
              <label>{tr("Client")}
                <select className="camp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">{tr("toți clienții")}</option>
                  {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
                </select>
              </label>
            )}
            <label className="comutator-dezactivate">
              <input type="checkbox" checked={doarDeschise}
                onChange={(e) => setDoarDeschise(e.target.checked)} />
              {tr("doar tichetele în lucru")}
            </label>
            <button className="buton buton-actiune" onClick={() => setFormNou({})}>
              {tr("tichet nou")}
            </button>
          </div>
        </div>
      </div>

      {taiat && (
        <p className="nota-cautare" style={{ marginBottom: ".6rem" }}>
          {tr("Se arată primele {0} tichete din {1}. Alege un client sau bifează „doar deschise\" ca să vezi restul.", taiat.plafon, taiat.total)}
        </p>
      )}

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">
          {doarDeschise ? tr("Niciun tichet în lucru.") : tr("Niciun tichet.")}
        </div></div>
      ) : (
        <div className="panou">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Număr")}</th><th>{tr("Titlu")}</th><th>{tr("Client / locație")}</th>
                <th>{tr("Severitate")}</th><th>{tr("Stare")}</th><th>{tr("Termene")}</th><th />
              </tr>
            </thead>
            <tbody>
              {lista.map((t) => (
                <tr key={t.id}>
                  <td><code>{t.numar}</code></td>
                  <td>
                    {tr(t.titlu)}
                    <span className="cod-mic">{t.tipTichet === "Incident" ? tr("incident") : tr("cerință")}</span>
                  </td>
                  <td>{tr(t.client?.denumire)}<br /><span className="fara-pret">{tr(t.locatie?.denumire)}</span></td>
                  <td>
                    {t.severitate ?? t.severitatePropusa}
                    {!t.severitate && <span className="marcaj" title={tr("Propusă automat din obiect, neconfirmată")}>{tr("de confirmat")}</span>}
                  </td>
                  <td>{tr(STARI[t.status] ?? t.status)}</td>
                  <td className="celula-ceasuri">
                    {(t.ceasuri ?? []).length === 0
                      ? <span className="fara-pret">{tr("fără SLA")}</span>
                      : ordineCeas(t.ceasuri).map((c) => <Ceas key={c.tip} c={c} />)}
                  </td>
                  <td className="celula-actiuni">
                    <button className="buton buton-mic buton-discret" onClick={() => deschide(t.id)}>
                      {tr("deschide")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formNou && (
        <TichetNou
          eu={eu} clienti={clienti}
          laInchidere={() => setFormNou(null)}
          laSalvare={(t) => { setFormNou(null); reincarca(); setDeschis(t); }}
        />
      )}

      {deschis && (
        <Detaliu
          eu={eu} tichet={deschis} dispecerat={dispecerat}
          laInchidere={() => setDeschis(null)}
          laSchimbare={async () => { await deschide(deschis.id); reincarca(); }}
        />
      )}
    </div>
  );
}

// ── Tichet nou ─────────────────────────────────────────────────────────────

function TichetNou({ eu, clienti, laInchidere, laSalvare }) {
  const [clientId, setClientId] = useState(eClient(eu.rol) ? eu.clientId ?? "" : "");
  const [arbore, setArbore] = useState([]);
  const [locatieId, setLocatieId] = useState("");
  const [obiect, setObiect] = useState("");
  const [tipTichet, setTipTichet] = useState("Incident");
  const [canal, setCanal] = useState("Portal");
  const [titlu, setTitlu] = useState("");
  const [descriere, setDescriere] = useState("");
  const [salveaza, setSalveaza] = useState(false);
  // Refuzul se arată ÎN modal: un banner în spatele lui e invizibil, iar
  // butonul pare că nu face nimic.
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    if (!clientId) { setArbore([]); return; }
    api.parc(clientId).then(setArbore).catch((e) => setEroare(mesajEroare(e)));
    setLocatieId(""); setObiect("");
  }, [clientId]);

  // Un client fără nicio locație în parc lasă un dropdown gol, iar utilizatorul
  // n-are de unde ști de ce. Se spune, nu se tace.
  const faraLocatii = !!clientId && arbore.length === 0;
  const locatia = arbore.find((l) => l.id === locatieId);
  const obiecte = locatia
    ? [
      ...(locatia.sisteme ?? []).map((s) => ({ v: `s:${s.id}`, e: `Sistem — ${s.denumire}` })),
      ...(locatia.sisteme ?? []).flatMap((s) => (s.echipamente ?? []).map(
        (ec) => ({ v: `e:${ec.id}`, e: `Echipament — ${ec.eticheta}` }))),
    ] : [];

  async function trimite() {
    setSalveaza(true); setEroare("");
    try {
      const t = await api.deschideTichet({
        clientId, locatieId, tipTichet, canal, titlu, descriere,
        ...(obiect.startsWith("s:") ? { sistemInstalatId: obiect.slice(2) } : {}),
        ...(obiect.startsWith("e:") ? { echipamentId: obiect.slice(2) } : {}),
      });
      laSalvare(t);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setSalveaza(false); }
  }

  /*
   * Ce mai lipsește se SPUNE, nu se lasă pe seama unui buton palid. Un buton
   * dezactivat fără explicație arată exact ca unul stricat: omul completează
   * tot ce vede pe ecran, apasă, nu se întâmplă nimic, și n-are de unde ști
   * că titlul lui de patru litere e prea scurt.
   *
   * Pragurile sunt cele din DTO-ul serverului (5 și 10), nu unele alese aici:
   * două praguri diferite ar însemna un formular care se lasă trimis și un
   * server care îl refuză.
   */
  const lipsesc = [
    !clientId && tr("clientul"),
    !locatieId && tr("locația"),
    titlu.trim().length < 5 && tr("un titlu de cel puțin 5 caractere"),
    descriere.trim().length < 10 && tr("o descriere de cel puțin 10 caractere"),
  ].filter(Boolean);
  const gata = lipsesc.length === 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Tichet nou")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}

        <div className="formular-linie">
          {!eClient(eu.rol) && (
            <label>{tr("Client")}
              <select className="camp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">{tr("— alege —")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </label>
          )}
          <label className="camp">{tr("Locație")}
            <select value={locatieId} onChange={(e) => { setLocatieId(e.target.value); setObiect(""); }}
              disabled={faraLocatii}>
              <option value="">{faraLocatii ? tr("— clientul n-are nicio locație —") : tr("— alege —")}</option>
              {arbore.map((l) => <option key={l.id} value={l.id}>{tr(l.denumire)}</option>)}
            </select>
          </label>
        </div>

        <div className="formular-linie">
          <label className="camp">{tr("Obiectul afectat")}
            <select value={obiect} onChange={(e) => setObiect(e.target.value)}
              disabled={!locatieId}>
              <option value="">{tr("— niciunul precizat —")}</option>
              {obiecte.map((o) => <option key={o.v} value={o.v}>{o.e}</option>)}
            </select>
          </label>
          <label className="camp">{tr("Tip")}
            <select value={tipTichet} onChange={(e) => setTipTichet(e.target.value)}>
              {TIPURI.map(([v, e]) => <option key={v} value={v}>{tr(e)}</option>)}
            </select>
          </label>
          {!eClient(eu.rol) && (
            <label className="camp">{tr("Canal")}
              <select value={canal} onChange={(e) => setCanal(e.target.value)}>
                {CANALE.map((c) => <option key={c} value={c}>{tr(c)}</option>)}
              </select>
            </label>
          )}
        </div>

        {faraLocatii && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Clientul n-are nicio locație în parcul instalat.")}</strong>{" "}
            {tr("Un tichet se deschide pe o locație, deci întâi se adaugă locația la „Clienți\", apoi tichetul.")}
          </div></div>
        )}

        <p className="nota-cautare">
          {tr("Obiectul decide amploarea, deci coloana din matricea SLA — un sistem întreg nu se tratează la fel ca un echipament. Poate fi ales cel mult unul.")}
        </p>

        <label className="camp">{tr("Titlu")}
          <input value={titlu} onChange={(e) => setTitlu(e.target.value)}
            placeholder={tr("Ce s-a întâmplat, pe scurt")} />
        </label>
        <label className="camp">{tr("Descriere")}
          <textarea className="camp-textarea" rows={5} value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            placeholder={tr("De când, ce se vede, ce s-a încercat deja")} />
        </label>

        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune" disabled={!gata || salveaza} onClick={trimite}>
            {salveaza ? tr("se deschide…") : tr("deschide tichetul")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detaliul tichetului ────────────────────────────────────────────────────

function Detaliu({ eu, tichet, dispecerat, laInchidere, laSchimbare }) {
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [notaInterna, setNotaInterna] = useState(false);
  const [propunere, setPropunere] = useState(null);
  const [dosarDeschis, setDosarDeschis] = useState(false);
  const [severitate, setSeveritate] = useState(tichet.severitate ?? tichet.severitatePropusa ?? "Major");
  const [motivSeveritate, setMotivSeveritate] = useState("");

  const client = eClient(eu.rol);
  const inchis = ["Inchis", "Anulat", "Respins"].includes(tichet.status);
  const inPauza = (tichet.ceasuri ?? []).some((c) => c.inPauza);
  const deschisa = (tichet.propuneri ?? []).find((p) => p.status === "Propusa");

  async function fa(actiune) {
    setLucreaza(true); setEroare("");
    try { await actiune(); await laSchimbare(); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>
          <code>{tichet.numar}</code> · {tr(tichet.titlu)}
          <span className="eticheta" style={{ marginLeft: ".5rem" }}>{tr(STARI[tichet.status] ?? tichet.status)}</span>
        </h3>
        <p className="subtitlu">
          {tr(tichet.client?.denumire)} · {tr(tichet.locatie?.denumire)}
          {tichet.sistemInstalat ? ` · ${tichet.sistemInstalat.denumire}` : ""}
          {tichet.echipament ? ` · ${tichet.echipament.eticheta}` : ""}
          {tr(" · deschis ")}{dataOra(tichet.deschisLa)}
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        {(tichet.ceasuri ?? []).length === 0 ? (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Tichetul n-are termene asumate.")}</strong>{" "}
            {tichet.deCeFaraSla ?? tr("Clientul n-are un contract activ cu profil SLA.")}
          </div></div>
        ) : (
          <div className="ceasuri-mari">
            {ordineCeas(tichet.ceasuri).map((c) => <Ceas key={c.tip} c={c} />)}
          </div>
        )}

        <div className="panou">
          <div className="panou-antet">{tr("Descriere")}</div>
          <div className="panou-corp" style={{ whiteSpace: "pre-wrap" }}>{tr(tichet.descriere)}</div>
        </div>

        {/* Clasificarea e a dispeceratului: ea decide rândul din matricea SLA. */}
        {dispecerat && !inchis && (
          <div className="panou">
            <div className="panou-antet">{tr("Severitate")}</div>
            <div className="panou-corp">
              <div className="formular-linie">
                <label className="camp">{tr("Treaptă")}
                  <select value={severitate} onChange={(e) => setSeveritate(e.target.value)}>
                    {SEVERITATI.map((s) => <option key={s} value={s}>{tr(s)}</option>)}
                  </select>
                </label>
                <label className="camp">{tr("Motiv (obligatoriu la schimbare)")}
                  <input value={motivSeveritate}
                    onChange={(e) => setMotivSeveritate(e.target.value)}
                    placeholder={tr("De ce nu e treapta propusă automat")} />
                </label>
              </div>
              <p className="nota-cautare">
                {tr("Reclasificarea se consemnează, dar NU rescrie termenele deja promise.")}
              </p>
              <button className="buton buton-mic" disabled={lucreaza}
                onClick={() => fa(() => api.confirmaSeveritate(tichet.id, {
                  severitate, ...(motivSeveritate.trim() ? { motiv: motivSeveritate.trim() } : {}),
                }))}>
                {tr("confirmă severitatea")}
              </button>
            </div>
          </div>
        )}

        {/* Propunerea de vizită și decizia clientului pe ea. */}
        <div className="panou">
          <div className="panou-antet">{tr("Vizite propuse")}</div>
          <div className="panou-corp">
            {(tichet.propuneri ?? []).length === 0 && <div className="gol">{tr("Nicio propunere încă.")}</div>}
            {(tichet.propuneri ?? []).map((p) => (
              <div key={p.id} className="propunere">
                <div>
                  <strong>{String(p.dataPropusa).slice(0, 10)}</strong>
                  {p.oraStart ? tr(" de la {0}", p.oraStart) : ""}
                  {p.tehnician ? ` · ${p.tehnician.numeComplet}` : p.echipa ? ` · ${p.echipa.denumire}` : ""}
                  <span className="eticheta" style={{ marginLeft: ".5rem" }}>{eticheta(STARI_PROPUNERE, p.status)}</span>
                  {p.justificarePesteTermen && (
                    <div className="fara-pret">{tr("peste termen:")} {p.justificarePesteTermen}</div>
                  )}
                  {p.motivRefuz && <div className="fara-pret">{tr("refuz:")} {p.motivRefuz}</div>}
                </div>
                {p.status === "Propusa" && !inchis && (
                  <div className="actiuni-catalog">
                    <button className="buton buton-mic buton-actiune" disabled={lucreaza}
                      onClick={() => fa(() => api.decideVizita(tichet.id, p.id, { accept: true }))}>
                      {tr("acceptă")}
                    </button>
                    <button className="buton buton-mic buton-sters" disabled={lucreaza}
                      onClick={() => {
                        const motiv = window.prompt("De ce nu se potrivește data propusă?");
                        if (motiv && motiv.trim().length >= 5) {
                          fa(() => api.decideVizita(tichet.id, p.id, { accept: false, motivRefuz: motiv.trim() }));
                        }
                      }}>
                      {tr("nu se potrivește")}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {dispecerat && !inchis && !deschisa && (
              <button className="buton buton-mic" style={{ marginTop: ".6rem" }}
                onClick={() => setPropunere({})}>
                {tr("propune o vizită")}
              </button>
            )}
          </div>
        </div>

        {/* Firul de mesaje. Ce e notă internă rămâne internă. */}
        <div className="panou">
          <div className="panou-antet">{tr("Corespondență")}</div>
          <div className="panou-corp">
            {(tichet.mesaje ?? []).length === 0 && <div className="gol">{tr("Nimic scris încă.")}</div>}
            {(tichet.mesaje ?? []).map((m) => (
              <div key={m.id} className={"mesaj" + (m.vizibilClient ? "" : " mesaj-intern")}>
                <div className="fara-pret">
                  {dataOra(m.createdAt)}{m.vizibilClient ? "" : tr(" · notă internă")}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
              </div>
            ))}
            {!inchis && (
              <>
                <textarea className="camp-textarea camp-lat" rows={3} value={mesaj}
                  onChange={(e) => setMesaj(e.target.value)} placeholder={tr("Scrie un mesaj…")} />
                <div className="linie-actiune" style={{ alignItems: "center" }}>
                  {!client && (
                    <label className="comutator-dezactivate">
                      <input type="checkbox" checked={notaInterna}
                        onChange={(e) => setNotaInterna(e.target.checked)} />
                      {tr("notă internă — clientul nu o vede")}
                    </label>
                  )}
                  <button className="buton buton-mic buton-actiune"
                    disabled={lucreaza || !mesaj.trim()}
                    onClick={() => fa(async () => {
                      await api.scrieMesajTichet(tichet.id, {
                        text: mesaj.trim(), vizibilClient: client ? true : !notaInterna,
                      });
                      setMesaj("");
                    })}>
                    {tr("trimite")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {(tichet.vizite ?? []).length > 0 && (
          <div className="panou">
            <div className="panou-antet">{tr("Intervenții legate")}</div>
            <div className="panou-corp">
              {tichet.vizite.map((v) => (
                <div key={v.id}><code>{v.numar}</code> · {eticheta(STARI_VIZITA, v.status)}
                  {v.dataProgramata ? ` · ${String(v.dataProgramata).slice(0, 10)}` : ""}</div>
              ))}
              {/*
                * Butonul apare de la PRIMA vizită, nu doar la închidere: o
                * intervenție cu trei drumuri se citește cel mai greu chiar cât
                * timp e în curs, iar dosarul spune el însuși că nu e documentul
                * final până la închidere.
                */}
              <div className="linie-actiune">
                <button className="buton-mic" onClick={() => setDosarDeschis(true)}>
                  {tr("vezi dosarul intervenției")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dosarul se deschide PESTE detaliul tichetului, ca omul să se întoarcă
            unde era, nu să reia navigarea de la listă. */}
        {dosarDeschis && (
          <DosarInterventie tichetId={tichet.id} laInchidere={() => setDosarDeschis(false)} />
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide fereastra")}</button>
          {dispecerat && !inchis && tichet.status === "Deschis" && (
            <button className="buton" disabled={lucreaza}
              onClick={() => fa(() => api.preiaTichet(tichet.id))}>{tr("preia")}</button>
          )}
          {dispecerat && !inchis && (inPauza ? (
            <button className="buton" disabled={lucreaza}
              onClick={() => fa(() => api.reiaTichet(tichet.id))}>{tr("reia ceasul")}</button>
          ) : (
            <button className="buton" disabled={lucreaza}
              onClick={() => {
                const motiv = window.prompt("De ce se oprește ceasul? (așteptăm piesa, accesul, aprobarea…)");
                if (motiv && motiv.trim().length >= 5) fa(() => api.pauzeazaTichet(tichet.id, motiv.trim()));
              }}>
              {tr("pune ceasul pe pauză")}
            </button>
          ))}
          {dispecerat && !inchis && (
            <button className="buton buton-sters buton-actiune buton-terminal" disabled={lucreaza}
              onClick={() => {
                const motiv = window.prompt(
                  "Închidere. Dacă tichetul n-are o fișă finalizată, scrie motivul (min. 10 caractere); altfel lasă gol.");
                if (motiv === null) return;
                fa(() => api.inchideTichet(tichet.id, motiv.trim() || undefined));
              }}>
              {tr("închide tichetul")}
            </button>
          )}
        </div>

        {propunere && (
          <PropuneVizita
            tichetId={tichet.id}
            laInchidere={() => setPropunere(null)}
            laSalvare={async () => { setPropunere(null); await laSchimbare(); }}
          />
        )}
      </div>
    </div>
  );
}

function PropuneVizita({ tichetId, laInchidere, laSalvare }) {
  const [dataPropusa, setDataPropusa] = useState("");
  const [oraStart, setOraStart] = useState("09:00");
  const [tehnicieni, setTehnicieni] = useState([]);
  const [tehnicianId, setTehnicianId] = useState("");
  const [justificare, setJustificare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  useEffect(() => { api.tehnicieni().then(setTehnicieni).catch(() => {}); }, []);

  async function trimite() {
    setLucreaza(true); setEroare("");
    try {
      await api.propuneVizita(tichetId, {
        dataPropusa, oraStart,
        ...(tehnicianId ? { tehnicianId } : {}),
        ...(justificare.trim() ? { justificarePesteTermen: justificare.trim() } : {}),
      });
      await laSalvare();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Propune o vizită")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}
        <div className="formular-linie">
          <label className="camp">{tr("Data")}
            <input type="date" value={dataPropusa}
              onChange={(e) => setDataPropusa(e.target.value)} />
          </label>
          <label className="camp">{tr("Ora")}
            <input type="time" value={oraStart}
              onChange={(e) => setOraStart(e.target.value)} />
          </label>
        </div>
        <label className="camp">{tr("Tehnician")}
          <select value={tehnicianId} onChange={(e) => setTehnicianId(e.target.value)}>
            <option value="">{tr("— se decide mai târziu —")}</option>
            {tehnicieni.map((t) => <option key={t.id} value={t.id}>{t.numeComplet}</option>)}
          </select>
        </label>
        <label className="camp">{tr("Justificare, dacă data iese din termen")}
          <textarea className="camp-textarea" rows={3} value={justificare}
            onChange={(e) => setJustificare(e.target.value)}
            placeholder={tr("Aplicația o cere doar când propunerea depășește termenul de restaurare.")} />
        </label>
        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune" disabled={lucreaza || !dataPropusa} onClick={trimite}>
            {lucreaza ? tr("se trimite…") : tr("propune")}
          </button>
        </div>
      </div>
    </div>
  );
}
